import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import logo from '@assets/images/logo.svg';
import NavigationTop from '@components/Navigation/NavigationTop';
import ProfileMenu from '@components/ProfileMenu/Header';
import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Text } from '@groupeactual/ui-kit';
import {
  AppBar,
  Box,
  Container,
  Grid,
  IconButton,
  Link,
  Toolbar,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { getUser } from '@stores/selectors/user.selector';
import Constants from 'constants/constants';
import OverrideTheme from 'themes/custom-theme-helper';

function colorEnv() {
  switch (process.env.VITE_APP_BUILD_ENV) {
    case 'local':
      return OverrideTheme.colors.success;
    case 'preprod':
      return OverrideTheme.colors.warning;
    case 'development':
      return OverrideTheme.colors.error;
    default:
      return 'inherit';
  }
}

const useStyles = makeStyles(() => ({
  appBarColor: {
    ...OverrideTheme.ui.headerPrimary(),
  },
  title: {
    flexGrow: 1,
    color: OverrideTheme.colors.textPrimary,
    fontWeight: 700,
    lineHeight: 1.2,
  },
  logoSvg: {
    color: OverrideTheme.colors.actual,
    fontSize: '2rem',
    verticalAlign: 'middle',
  },
  logoLeft: {
    color: OverrideTheme.colors.textPrimary,
    fontWeight: 400,
  },
  navigationAppBar: {
    ...OverrideTheme.ui.lightCard(),
    borderRadius: 0,
    color: OverrideTheme.colors.textPrimary,
  },
  navigationToolbar: {
    minHeight: 40,
  },
  navigationList: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-around',
    '& a': {
      color: OverrideTheme.colors.textPrimary,
    },
  },
  navigationListItemIcon: {
    minWidth: '30px',
    padding: '5px 0',
  },
  envColor: {
    color: colorEnv(),
  },
  profileBtn: {
    borderRadius: 100,
    padding: `${OverrideTheme.getSpacing(1.3)}px`,
    backgroundColor: 'white',
    border: 'none !important',
    fontSize: `${OverrideTheme.getSpacing(0.24)}rem`,
    color: OverrideTheme.colors.primary,
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    width: 150,
    height: 40,
  },
}));

const Header = (): JSX.Element => {
  const user = useSelector(getUser);
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Box mb={3}>
      <AppBar position="static" className={classes.appBarColor}>
        <Container maxWidth="xl">
          <Toolbar>
            <Grid container spacing={1} alignItems="center">
              <Grid item>
                <img src={logo} alt="Nina Logo" />
              </Grid>
              <Grid item>
                <Link href={Constants.Routes.HOME_PATH}>
                  <Typography className={classes.title} variant="h3">
                    Nina
                    {process.env.VITE_APP_BUILD_ENV !== 'production' && (
                      <small className={classes.envColor}>
                        {' ( ' +
                          process.env.VITE_APP_BUILD_ENV?.toUpperCase() +
                          ' ) '}
                      </small>
                    )}
                  </Typography>
                </Link>
              </Grid>
            </Grid>
            <Box marginRight={2}>
              <Link
                href={
                  Constants.Routes.NOTION_PATH +
                  '/' +
                  Constants.System.NINA_NOTION_PAGE_ID
                }
              >
                <IconButton className={classes.profileBtn} centerRipple={false}>
                  <FontAwesomeIcon icon={faCircleQuestion} size="xl" />
                  <Text variant={'body1Regular'}>
                    {t('profile_menu.link.needHelp')}
                  </Text>
                </IconButton>
              </Link>
            </Box>
            {user ? <ProfileMenu /> : <></>}
          </Toolbar>
        </Container>
      </AppBar>
      <NavigationTop />
    </Box>
  );
};

export default Header;
